// Presentation Mode keeps the original cards and stream; only their presentation changes.
export const detailIndex = (index, length) => ((index % length) + length) % length;
export const detailTiming = { idle: 16000, thumbnail: 7500, crossfade: 1000, interaction: 5000, slide: 1200, loopReturn: 4000, arrow: 1200 / 1.33 };

if (typeof customElements !== 'undefined' && !customElements.get('rr-detail-slideshow')) {
  customElements.define('rr-detail-slideshow', class extends HTMLElement {
    connectedCallback() {
      this.list = document.getElementById(this.getAttribute('for'));
      if (!this.list) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.desktop = matchMedia('(min-width: 1000px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.finePointer = matchMedia('(hover: hover) and (pointer: fine)');
      window.addEventListener('pointermove', event => {
        if (this.launchCursorLocked) return;
        this.resetCursor();
        if (event.movementX || event.movementY) this.interact();
      }, options);
      for (const type of ['pointerdown', 'wheel', 'keydown', 'touchstart']) {
        window.addEventListener(type, () => this.resetCursor(), { ...options, passive: true });
      }
      window.addEventListener('keydown', () => this.interact(), options);
      document.addEventListener('focusin', event => {
        if (event.target.closest?.('.header, .rr-collection-header')) this.interact();
      }, options);
      document.addEventListener('focusout', () => queueMicrotask(() => this.schedule()), options);
      this.cards = []; this.index = 0; this.resumeAt = performance.now() + detailTiming.interaction; this.animations = [];
      this.openDrawers = new Set([...document.querySelectorAll('drawer[open], #facets-drawer[open], #cart-drawer[open], #header-sidebar[open], #country-drawer[open]')]);
      if ([...this.openDrawers].some(drawer => drawer.id === 'facets-drawer')) this.paused = true;
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        this.interact();
        if (button.hasAttribute('data-detail-step')) this.move(Number(button.dataset.detailStep), true);
        else {
          this.paused = !this.paused;
          button.setAttribute('aria-pressed', String(this.paused));
          button.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
          this.schedule();
        }
      }, options);
      document.addEventListener('click', event => {
        const button = event.target.closest?.('[data-slideshow-playback]');
        if (button) this.toggleDrawerPlayback(button);
      }, options);
      this.list.addEventListener('focusin', () => this.schedule(), options);
      this.list.addEventListener('focusout', () => queueMicrotask(() => this.interact()), options);
      // Wheel and touch remain native page scrolling; mouse drag mirrors the home carousel.
      this.list.addEventListener('pointerdown', event => {
        if (!this.enabled || event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button,input,select,textarea')) return;
        this.suppressClick = false;
        this.drag = { y: event.clientY, id: event.pointerId }; this.interact();
      }, options);
      window.addEventListener('pointerup', event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const distance = event.clientY - this.drag.y;
        this.drag = null;
        if (Math.abs(distance) > 40) { this.suppressClick = true; this.move(distance < 0 ? 1 : -1, true); }
        this.schedule();
      }, options);
      window.addEventListener('pointercancel', () => { this.drag = null; this.schedule(); }, options);
      this.list.addEventListener('dragstart', event => { if (this.enabled) event.preventDefault(); }, options);
      this.list.addEventListener('click', event => {
        if (this.suppressClick) { event.preventDefault(); event.stopImmediatePropagation(); this.suppressClick = false; }
      }, { ...options, capture: true });
      // Theme dialog lifecycle events do not bubble; capture observes drawer replacement too.
      document.addEventListener('dialog:before-show', event => { this.openDrawers.add(event.target); if (event.target.id === 'facets-drawer') this.paused = true; this.finish(); this.schedule(); }, { ...options, capture: true });
      document.addEventListener('dialog:after-hide', event => { this.openDrawers.delete(event.target); this.interact(); }, { ...options, capture: true });
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.finish(); this.schedule(); }, options);
      document.addEventListener('rr:slideshow-preferences-change', () => {
        this.finish(); this.resetCursor(false); this.interact();
      }, options);
      document.addEventListener('rr:grid-density-change', event => this.refresh(event.detail?.anchor), options);
      document.addEventListener('rr:collection-cards-sorted', () => this.refresh(), options);
      this.desktop.addEventListener('change', () => this.refresh(), options);
      this.reduced.addEventListener('change', () => { this.finish(); this.schedule(); }, options);
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this.list, { childList: true });
      this.resize = new ResizeObserver(() => this.positionControls());
      this.resize.observe(this.list);
      const cancelForInput = () => { this.finish(); this.interact(); };
      window.addEventListener('wheel', cancelForInput, { ...options, passive: true });
      window.addEventListener('touchstart', cancelForInput, { ...options, passive: true });
      window.addEventListener('keydown', event => {
        if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) cancelForInput();
      }, options);
      window.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateViewport();
          if (!this.moving) {
            if (this.motionEndY != null && Math.abs(window.scrollY - this.motionEndY) < 1) this.schedule();
            else this.interact();
          }
        });
      }, { ...options, passive: true });
      this.refresh();
    }
    syncPlaybackControls() {
      const headerPlayVisible = !!(this.enabled && this.autoplayEnabled && !this.launchCursorLocked &&
        (this.paused || this.openDrawers.size || this.resumeAt > performance.now()));
      if (document.documentElement.dataset.rrSlideshowPaused !== String(headerPlayVisible)) {
        document.documentElement.dataset.rrSlideshowPaused = String(headerPlayVisible);
        document.dispatchEvent?.(new CustomEvent('rr:slideshow-playback-state'));
      }
      for (const button of document.querySelectorAll?.('[data-slideshow-playback]') || []) {
        const playing = button.hasAttribute?.('data-slideshow-header-play') ? !headerPlayVisible : !!this.drawerPlayback && !this.paused;
        button.dataset.playing = String(playing);
        button.setAttribute('aria-label', playing ? button.dataset.pauseLabel : button.dataset.playLabel);
        button.disabled = !this.enabled || !this.autoplayEnabled;
      }
    }
    async toggleDrawerPlayback(button) {
      if (!this.enabled || !this.autoplayEnabled || this.startingPlayback) return;
      this.startingPlayback = true;
      this.paused = true;
      this.finish(); this.schedule();
      this.launchCursorLocked = true;
      clearTimeout(this.cursorTimer); this.cursorTimer = null; this.cursorHidden = true;
      document.documentElement.classList?.add('rr-detail-cursor-hidden', 'rr-detail-launching', 'rr-detail-header-hidden');
      try {
        await (button.closest('#facets-drawer') || document.querySelector?.('#facets-drawer'))?.hide();
        if (!this.isConnected || !this.enabled || !this.autoplayEnabled) return;
        document.activeElement?.blur?.();
        this.drawerPlayback = true; this.paused = false; this.resumeAt = 0;
        this.updateViewport();
        this.move(0, true);
        this.syncPlaybackControls();
      } finally {
        this.startingPlayback = false;
        if (!this.moving) this.releaseLaunchCursor();
      }
    }
    disconnectedCallback() {
      this.abort?.abort(); this.observer?.disconnect(); this.resize?.disconnect(); this.visibility?.disconnect();
      cancelAnimationFrame(this.scrollFrame); this.disable();
    }
    refresh(preferredHandle) {
      if (this.moving && this.desktop.matches && document.documentElement.dataset.rrGridDensity === '1') { this.pendingRefresh = true; return; }
      const previous = this.cards[this.index];
      this.finish();
      const cards = [...this.list.querySelectorAll(':scope > product-card')];
      const enabled = this.desktop.matches && document.documentElement.dataset.rrGridDensity === '1' && cards.length > 1;
      if (!enabled) { this.disable(); this.cards = cards; return; }
      this.cards = cards;
      const saved = preferredHandle || window.rrCollectionReturnPosition?.anchor;
      const restored = saved ? cards.findIndex(card => card.getAttribute('handle') === saved) : -1;
      this.index = restored >= 0 ? restored : Math.max(0, cards.indexOf(previous));
      this.enabled = true;
      this.list.classList.add('rr-detail-slides');
      this.hidden = false;
      this.show(); this.updateViewport(); this.schedule();
    }
    disable() {
      this.stopThumbnails(); this.finish(); this.enabled = false; this.hidden = true;
      this.resetCursor(false);
      document.documentElement.classList?.remove('rr-detail-intro-hidden');
      this.list?.classList.remove('rr-detail-slides');
      for (const card of this.cards || []) {
        card.removeAttribute('data-detail-active'); card.removeAttribute('aria-hidden'); card.inert = false;
      }
    }
    updateViewport() {
      if (!this.enabled || !this.cards.length) return;
      const center = innerHeight / 2;
      let nearest = 0, distance = Infinity;
      this.cards.forEach((card, index) => {
        const bounds = card.getBoundingClientRect();
        const delta = Math.abs(bounds.top + bounds.height / 2 - center);
        if (delta < distance) { nearest = index; distance = delta; }
      });
      if (!this.moving) this.index = nearest;
      const bounds = this.cards[nearest].getBoundingClientRect();
      this.visible = window.scrollY <= 1 || Math.min(bounds.bottom, innerHeight) - Math.max(bounds.top, 0) >= Math.min(bounds.height, innerHeight) / 2;
      if (!this.moving) {
        const first = this.cards[0].getBoundingClientRect();
        document.documentElement.classList?.toggle('rr-detail-intro-hidden', first.top <= (innerHeight - first.height) / 2 + 2);
      }
      this.show(); this.positionControls();
    }
    positionControls() {
      if (!this.enabled) return;
      const parent = this.parentElement.getBoundingClientRect();
      const list = this.list.getBoundingClientRect();
      const center = Math.max(list.top + 64, Math.min(innerHeight / 2, list.bottom - 64));
      this.style.top = `${center - parent.top}px`;
    }
    show() {
      this.cards.forEach((card, index) => {
        const active = index === this.index;
        card.toggleAttribute('data-detail-active', active);
        card.removeAttribute('aria-hidden'); card.inert = false;
      });
      this.list.dataset.detailIndex = String(this.index);
      // Warm only the current and next card, retaining lazy loading for the rest.
      [this.cards[this.index], this.cards[detailIndex(this.index + 1, this.cards.length)]].forEach(card => {
        card?.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
      });
    }
    releaseLaunchCursor() {
      this.launchCursorLocked = false;
      document.documentElement.classList?.remove('rr-detail-launching');
    }
    finish() {
      this.releaseLaunchCursor();
      this.stopThumbnails(true);
      this.motionRevision = (this.motionRevision || 0) + 1;
      cancelAnimationFrame(this.motionFrame);
      this.animations?.forEach(animation => animation.cancel()); this.animations = [];
      this.moving = false;
      if (this.enabled && this.cards.length) this.show();
    }
    move(step, manual = false) {
      if (!this.desktop.matches || !this.enabled || this.moving || this.drag || (this.openDrawers.size && !manual)) return;
      this.stopThumbnails(!manual);
      if (this.index >= this.cards.length - 3) this.list.dispatchEvent(new CustomEvent('rr:detail-needs-more'));
      let targetIndex = this.index + step;
      let returning = false;
      const firstBounds = this.cards[0].getBoundingClientRect();
      // From the page introduction, give the first jewel its own centered stop.
      if (step > 0 && this.index === 0 && firstBounds.top > (innerHeight - firstBounds.height) / 2 + 2) targetIndex = 0;
      if (targetIndex >= this.cards.length) {
        // The stream keeps its loading control until every batch has been appended.
        if (this.parentElement.querySelector('.rr-collection-more')) { this.schedule(); return; }
        targetIndex = 0;
        returning = true;
      }
      if (targetIndex < 0) { this.schedule(); return; }
      this.index = targetIndex;
      const next = this.cards[this.index];
      this.show();
      const start = window.scrollY;
      const bounds = next.getBoundingClientRect();
      const target = Math.max(0, Math.min(start + bounds.top - (innerHeight - bounds.height) / 2,
        document.documentElement.scrollHeight - innerHeight));
      if (this.reduced.matches) { window.scrollTo({top: target, behavior: 'instant'}); this.updateViewport(); this.schedule(); return; }
      document.documentElement.classList?.add('rr-detail-intro-hidden');
      if (!manual) document.documentElement.classList?.add('rr-detail-header-hidden');
      this.moving = true;
      const revision = this.motionRevision;
      const duration = returning ? detailTiming.loopReturn : manual ? detailTiming.arrow : detailTiming.slide;
      const began = performance.now();
      const tick = now => {
        if (!this.enabled || !this.moving || revision !== this.motionRevision) return;
        const progress = Math.min(1, (now - began) / duration);
        const eased = returning ? (1 - Math.cos(Math.PI * progress)) / 2
          : progress < .5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
        window.scrollTo({top: start + (target - start) * eased, behavior: 'instant'});
        if (progress < 1) this.motionFrame = requestAnimationFrame(tick);
        else {
          this.motionEndY = window.scrollY;
          this.finish(); this.updateViewport();
          if (this.pendingRefresh) { this.pendingRefresh = false; this.refresh(); }
          this.schedule();
        }
      };
      this.motionFrame = requestAnimationFrame(tick);
    }

    interact(resume = true) {
      if (this.launchCursorLocked) return;
      this.stopThumbnails();
      if (resume) this.resumeAt = performance.now() + detailTiming.interaction;
      this.schedule();
    }
    get autoplayEnabled() { return this.desktop.matches && window.rrSlideshowPreferences?.enabled !== false; }
    get viewingDuration() {
      const duration = window.rrSlideshowPreferences?.duration;
      return typeof duration === 'number' && duration >= 4000 && duration <= 16000 ? duration : detailTiming.idle;
    }
    cursorCanHide() {
      return this.enabled && this.autoplayEnabled && this.visible && this.finePointer?.matches
        && !this.paused && !this.drag && !this.reduced.matches && !document.hidden
        && !this.openDrawers.size && !this.list.contains(document.activeElement) && !window.Shopify?.designMode;
    }
    resetCursor(rearm = true) {
      if (this.launchCursorLocked) return;
      clearTimeout(this.cursorTimer); this.cursorTimer = null; this.cursorHidden = false;
      document.documentElement.classList?.remove('rr-detail-cursor-hidden');
      if (rearm && this.cursorCanHide()) this.cursorTimer = setTimeout(() => {
        this.cursorTimer = null;
        if (this.cursorCanHide()) {
          this.cursorHidden = true;
          document.documentElement.classList?.add('rr-detail-cursor-hidden');
        }
      }, 5000);
    }
    schedule() {
      this.syncPlaybackControls();
      if (!this.cursorCanHide()) this.resetCursor(false);
      else if (!this.cursorTimer && !this.cursorHidden) this.resetCursor();
      if (!this.enabled || !this.autoplayEnabled || !this.visible || this.moving || this.paused || this.drag || this.reduced.matches || document.hidden || this.openDrawers.size || this.list.contains(document.activeElement) || document.activeElement?.closest?.('.header, .rr-collection-header') || window.Shopify?.designMode) {
        this.stopThumbnails(); return;
      }
      if (this.thumbnailCard === this.cards[this.index]) return;
      clearTimeout(this.timer);
      const delay = Math.max(0, this.resumeAt - performance.now());
      if (delay > 0) { this.timer = setTimeout(() => this.schedule(), delay); return; }
      const bounds = this.cards[this.index].getBoundingClientRect();
      const centeredY = Math.max(0, Math.min(window.scrollY + bounds.top - (innerHeight - bounds.height) / 2,
        document.documentElement.scrollHeight - innerHeight));
      if (Math.abs(centeredY - window.scrollY) > 2) { this.move(0); return; }
      this.startThumbnails();
    }
    stopThumbnails(preserve = false) {
      clearTimeout(this.timer); clearTimeout(this.thumbnailTimer);
      if (!preserve) {
        if (!this.launchCursorLocked) document.documentElement.classList?.remove('rr-detail-header-hidden');
        for (const card of this.cards || []) card.removeAttribute('data-detail-thumbnail');
      }
      this.thumbnailCard = null;
    }
    startThumbnails() {
      this.stopThumbnails(true);
      const card = this.cards[this.index];
      this.thumbnailCard = card;
      document.documentElement.classList?.add('rr-detail-header-hidden');
      const secondary = card.querySelectorAll('.product-card__image--secondary')[0];
      if (secondary) {
        card.setAttribute('data-detail-thumbnail', 'primary');
        this.thumbnailTimer = setTimeout(() => {
          if (this.thumbnailCard === card && secondary.complete && secondary.naturalWidth > 0) {
            card.setAttribute('data-detail-thumbnail', 'secondary');
          }
        }, (this.viewingDuration - detailTiming.crossfade) / 2);
      }
      this.timer = setTimeout(() => this.move(1), this.viewingDuration);
    }
  });
}
